import React, { useState, useEffect, useRef } from 'react';

type ChatMessage = {
  type: string;
  id: string;
  nick: string;
  text: string;
  ts: number;
};

type Props = {
  items: ChatMessage[];
  onSend: (text: string) => void;
};

export function ChatPane({ items, onSend }: Props) {
  const [text, setText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [items]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedText = text.trim();
    if (trimmedText && onSend) {
      onSend(trimmedText);
      setText('');
      setIsTyping(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value;
    setText(newText);
    setIsTyping(newText.length > 0);
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const getAvatarColor = (nick: string) => {
    const colors = [
      'from-red-500 to-pink-500',
      'from-blue-500 to-cyan-500', 
      'from-green-500 to-emerald-500',
      'from-purple-500 to-violet-500',
      'from-orange-500 to-yellow-500',
      'from-teal-500 to-blue-500',
      'from-indigo-500 to-purple-500',
      'from-pink-500 to-rose-500'
    ];
    const hash = nick.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const isSystemMessage = (message: ChatMessage) => {
    return message.type === 'SYSTEM' || message.nick === 'System';
  };

  const onlineUsers = new Set(
    items
      .filter(msg => !isSystemMessage(msg))
      .map(msg => msg.nick)
      .slice(-20) // Only consider recent messages for "online" status
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 p-6 border-b border-slate-800/40">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Live Chat</h2>
              <p className="text-slate-400">{items.length} messages</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-slate-400">Live</span>
          </div>
        </div>

        {/* Online users indicator */}
        {onlineUsers.size > 0 && (
          <div className="mt-4 p-3 bg-slate-800/30 rounded-xl border border-slate-700/30">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-sm text-slate-400">Active users ({onlineUsers.size})</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {Array.from(onlineUsers).slice(0, 6).map(nick => (
                <span 
                  key={nick}
                  className="px-2 py-1 bg-slate-700/50 text-slate-300 text-xs rounded-lg"
                >
                  {nick}
                </span>
              ))}
              {onlineUsers.size > 6 && (
                <span className="px-2 py-1 bg-slate-700/50 text-slate-400 text-xs rounded-lg">
                  +{onlineUsers.size - 6} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Messages */}
      <div 
        ref={containerRef}
        className="flex-1 overflow-y-auto px-6 py-4 space-y-4 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent"
      >
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <div className="w-16 h-16 bg-slate-800/50 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2v-6a2 2 0 012-2h8z" />
              </svg>
            </div>
            <p className="text-lg font-medium">No messages yet</p>
            <p className="text-sm">Be the first to say something!</p>
          </div>
        ) : (
          items.map((message, i) => {
            const isConsecutive = i > 0 && items[i-1].nick === message.nick && !isSystemMessage(message) && !isSystemMessage(items[i-1]);
            const timeDiff = i > 0 ? message.ts - items[i-1].ts : 0;
            const showTimestamp = !isConsecutive || timeDiff > 60000; // Show if different user or >1min gap
            const isSystem = isSystemMessage(message);
            
            if (isSystem) {
              return (
                <div key={i} className="flex justify-center">
                  <div className="bg-slate-800/40 text-slate-400 px-3 py-1 rounded-full text-sm border border-slate-700/30">
                    <span className="text-xs text-slate-500">{formatTime(message.ts)}</span>
                    <span className="mx-2">•</span>
                    {message.text}
                  </div>
                </div>
              );
            }
            
            return (
              <div key={i} className={`${isConsecutive && timeDiff < 60000 ? 'ml-12' : ''}`}>
                {showTimestamp && (
                  <div className="flex items-center space-x-3 mb-2">
                    {!isConsecutive && (
                      <div className={`w-8 h-8 bg-gradient-to-br ${getAvatarColor(message.nick)} rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-lg flex-shrink-0`}>
                        {message.nick.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex items-baseline space-x-2 min-w-0">
                      <span className="font-semibold text-white truncate">{message.nick}</span>
                      <span className="text-xs text-slate-500 flex-shrink-0">{formatTime(message.ts)}</span>
                    </div>
                  </div>
                )}
                
                <div className={`${!showTimestamp ? 'ml-12' : 'ml-12'} ${showTimestamp ? '' : '-mt-1'}`}>
                  <div className="bg-slate-800/40 text-slate-200 px-4 py-3 rounded-xl border border-slate-700/30 break-words hover:bg-slate-800/60 transition-colors">
                    {message.text}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 p-6 border-t border-slate-800/40">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="relative">
            <input
              ref={inputRef}
              className="w-full bg-slate-800/70 border border-slate-700/50 text-white px-4 py-3 pr-32 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 placeholder-slate-500"
              value={text}
              onChange={handleInputChange}
              placeholder="Type your message..."
              maxLength={500}
            />
            
            {/* Character counter */}
            <div className="absolute right-16 top-1/2 transform -translate-y-1/2 text-xs text-slate-500">
              {text.length}/500
            </div>
            
            {/* Send button */}
            <button
              type="submit"
              disabled={!text.trim()}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 disabled:from-slate-600 disabled:to-slate-600 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed btn-bounce"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
          
          {/* Typing indicator */}
          {isTyping && (
            <div className="flex items-center space-x-2 text-slate-400 text-sm">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
              <span>Typing...</span>
            </div>
          )}
        </form>

        {/* Quick actions */}
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            onClick={() => setText('Good luck everyone! 🍀')}
            className="text-xs bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 px-3 py-1 rounded-lg transition-colors"
          >
            Good luck! 🍀
          </button>
          <button
            onClick={() => setText('BINGO! 🎉')}
            className="text-xs bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 px-3 py-1 rounded-lg transition-colors"
          >
            BINGO! 🎉
          </button>
          <button
            onClick={() => setText('Nice one! 👏')}
            className="text-xs bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 px-3 py-1 rounded-lg transition-colors"
          >
            Nice! 👏
          </button>
        </div>
      </div>
    </div>
  );
}